/**
 * Per-pool throttle state, keyed by `ConnectionManager` pool key.
 *
 * Lives in a module-scoped Map so the counters persist across the React
 * remounts a rebuild triggers — if these were `useRef`s they'd reset to 0
 * on every reconnect and a flapping socket would degenerate into a tight
 * retry storm. The Map only grows by one entry per distinct module URI
 * and is never released; that's fine for the portal's handful of
 * connections.
 */
export type ReconnectThrottle = {
  /** Exponential-backoff attempt counter (resets after a stable connect). */
  attempts: number;
  /** Fast attempts spent; on slow keepalive cadence from here. */
  exhausted: boolean;
  /** `isFatal` latched; stop until a stable reconnect proves otherwise. */
  fatal: boolean;
  /** Has this pool entry ever been healthy? Guards the wake path so we
   *  don't force-reconnect an in-flight initial connect. */
  everActive: boolean;
  /** Last wake-driven force-reconnect — for dedupe between visibility +
   *  online events that often fire within milliseconds of each other. */
  lastWakeAt: number;
  /** Last silent-disconnect rebuild — feeds the watchdog cooldown. */
  lastSilentRebuildAt: number;
};

const store = new Map<string, ReconnectThrottle>();

export function getReconnectThrottle(key: string): ReconnectThrottle {
  let entry = store.get(key);
  if (!entry) {
    entry = {
      attempts: 0,
      exhausted: false,
      fatal: false,
      everActive: false,
      lastWakeAt: 0,
      lastSilentRebuildAt: 0,
    };
    store.set(key, entry);
  }
  return entry;
}
